import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Cctv, Link2, Video, VideoOff, AlertTriangle } from "lucide-react";

const STATUS = {
  online: { color: "#22C55E", label: "ONLINE", icon: Video },
  degraded: { color: "#F59E0B", label: "DEGRADED", icon: AlertTriangle },
  offline: { color: "#EF4444", label: "OFFLINE", icon: VideoOff },
};

function CamCard({ cam, onLink }) {
  const [cc, setCc] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTick(new Date()), 1000); return () => clearInterval(t); }, []);
  const st = STATUS[cam.status] || STATUS.offline;
  const link = async () => {
    if (!cc.trim()) { toast.error("Enter a CC# to link"); return; }
    setBusy(true);
    try {
      await client.post(`/watch/${cam.cam_id}/link`, { incident_cc: cc.trim() });
      toast.success(`${cam.cam_id} linked to ${cc.trim()} — logged on the call`);
      setCc(""); onLink();
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); setCc(""); }
    setBusy(false);
  };
  return (
    <div data-testid={`watch-cam-${cam.cam_id}`} className="border border-[#2C2D35] bg-[#141518] rounded-sm overflow-hidden">
      <div className="relative h-40 bg-[#0a0b0d] flex items-center justify-center overflow-hidden">
        {cam.status !== "offline" ? (
          <>
            <div className="absolute inset-0 opacity-30" style={{ background: "repeating-linear-gradient(0deg,#0f1115 0 2px,#161920 2px 4px)" }} />
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 40%, #1c2733 0%, transparent 65%)" }} />
            <Cctv size={34} className="text-[#374151] relative" />
            <span className="absolute top-2 left-2 flex items-center gap-1.5 font-mono text-[9px] text-white bg-black/60 px-1.5 py-0.5 rounded-sm">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: st.color }} /> {cam.cam_id} · DEMO FEED
            </span>
            <span className="absolute bottom-2 right-2 font-mono text-[9px] text-[#9CA3AF] bg-black/60 px-1.5 py-0.5 rounded-sm tabular-nums">
              {tick.toLocaleTimeString("en-GB", { hour12: false })}
            </span>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 text-[#4B5563]"><VideoOff size={28} /><span className="font-mono text-[9px] uppercase">No signal</span></div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2">
          <span className="font-head font-bold text-sm text-white">{cam.name}</span>
          <span className="ml-auto font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm border" style={{ color: st.color, borderColor: `${st.color}55` }}>{st.label}</span>
        </div>
        <div className="font-mono text-[9px] text-[#6B7280] mt-0.5">{cam.address} · {cam.kind?.replace(/_/g, " ")}</div>
        {cam.linked_calls?.length > 0 && (
          <div className="font-mono text-[9px] text-[#22D3EE] mt-1">Linked: {cam.linked_calls.slice(-3).map((l) => l.cc).join(", ")}</div>
        )}
        <div className="flex gap-1.5 mt-2">
          <input data-testid={`watch-link-cc-${cam.cam_id}`} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="CC# to link…"
            className="flex-1 bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-[10px] font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm" />
          <button data-testid={`watch-link-btn-${cam.cam_id}`} onClick={link} disabled={busy || cam.status === "offline"}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[9px] font-head font-bold bg-[#22D3EE] text-black rounded-sm disabled:opacity-40 uppercase"><Link2 size={11} /> Link to CAD</button>
        </div>
      </div>
    </div>
  );
}

export default function Watch() {
  const [data, setData] = useState(null);
  const load = () => client.get("/watch/cameras").then((r) => setData(r.data)).catch(() => setData({ cameras: [] }));
  useEffect(() => { load(); }, []);
  return (
    <Layout>
      <PageHeader title="MATCOM WATCH™" sub="Authorized camera network connected to CAD · demo feeds · link any camera to an active call" icon={Cctv} />
      <div className="flex-1 overflow-y-auto p-5" data-testid="watch-page">
        {data?.banner && (
          <div className="flex items-center gap-2 border border-[#22D3EE]/40 bg-[#22D3EE]/10 text-[#22D3EE] font-mono text-[10px] px-3 py-2 mb-4 rounded-sm">
            <Cctv size={13} /> {data.banner}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(data?.cameras || []).map((c) => <CamCard key={c.cam_id} cam={c} onLink={load} />)}
        </div>
      </div>
    </Layout>
  );
}
